import { Api } from '@/core/trpc'
import { useEffect } from 'react'

type AuthenticationStatus = 'unauthenticated' | 'loading' | 'authenticated'

export const useSession = () => {
  const session = Api.authentication.findSession.useQuery()

  const logout = Api.authentication.logout.useMutation()
  const setMetadata = Api.authentication.setMetadata.useMutation()

  const user = session.data?.user

  const isLoading = session.isLoading

  const isLoggedIn = !!user

  let status: AuthenticationStatus = 'unauthenticated'

  if (isLoading) {
    status = 'loading'
  } else if (isLoggedIn) {
    status = 'authenticated'
  }

  const ensureMetadata = async () => {
    if (user.timezone || setMetadata.isLoading) {
      return
    }

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

      await setMetadata.mutateAsync({ timezone })
    } catch (error) {
      // ignore
    }
  }

  const checkRole = (roleName: string) => {
    return !!(user?.globalRole === roleName)
  }

  const isAdmin = () => {
    return user?.globalRole === 'ADMIN'
  }

  const handleLogout = async () => {
    await logout.mutateAsync()

    session.remove()
  }

  useEffect(() => {
    if (isLoggedIn) {
      ensureMetadata()
    }
  }, [isLoggedIn])

  return {
    user,
    checkRole,
    isAdmin,
    refetch: session.refetch,
    logout: handleLogout,
    authenticationStatus: status,
    isLoggedIn,
    isLoading,
  }
}
